import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SidebarNavigationInterface } from "../../utils/constants/Interfaces.constants";
import "./sidebar.scss";
import SidebarConst from "../../utils/constants/sidebar.constants";

interface SidebarProps {
  item: SidebarNavigationInterface;
  isCollapsed: boolean;
  isSelected: boolean;
  onSelect: (id: string) => void;
  isOpenDropdown: boolean;
  onToggleDropdown: (id: string) => void; // New prop to handle dropdown toggle
}

const SidebarItem: React.FC<SidebarProps> = ({
  item,
  isCollapsed,
  isSelected,
  onSelect,
  isOpenDropdown,
  onToggleDropdown, // Get the dropdown toggle handler
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavigation = (ev: React.MouseEvent<HTMLAnchorElement>, route: string) => {
    ev.preventDefault();
    if (location.pathname === route) return;
    onSelect(item.id.toString());
    navigate(route);
  };

  const toggleOpen = () => {
    onToggleDropdown(item.id.toString()); // Use the handler to toggle dropdown
    onSelect(item.id.toString()); // Set the selected item
  };

  const isActiveRoute = (path: string) => location.pathname.includes(path);

  return (
    <div>
      {item.subItems.length === 0 ? (
        <Link
          to={item.routePath}
          onClick={(ev) => handleNavigation(ev, item.routePath)}
          className={
            isActiveRoute(item.routePath) || isSelected
              ? "sidebar-link-active px-2 sidebar-link-item d-flex no-text-decoration"
              : "sidebar-link-item px-2 d-flex no-text-decoration"
          }
        >
          <img
            width={18}
            height={18}
            className={isActiveRoute(item.routePath) || isSelected ? "icon-black" : "icon-white"}
            src={item.iconURL}
            alt={item.routeLabel}
          />
          {!isCollapsed && (
            <p className="route-label mb-0 name-color text-capitalize weight-400 ms-2">
              {item.routeLabel}
            </p>
          )}
        </Link>
      ) : (
        <div>
          <div
            className={`drop-down ${isOpenDropdown ? "sidebar-link-active" : "sidebar-link-item"}`}
            onClick={toggleOpen}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              color: "white",
              marginBottom: 2,
            }}
          >
            <div className="px-2" style={{ display: "flex", gap: "12px", flex: 1 }}>
              <img
                src={item.iconURL}
                alt={item.routeLabel}
                style={{ width: "18px", height: "18px" }}
                className={isActiveRoute(item.routePath) || isOpenDropdown ? "icon-black" : "icon-white"}
              />
              {!isCollapsed && (
                <p className="mb-0 text-capitalize collaps-style weight-300">
                  {item.routeLabel}
                  <span className={`sidebar-toggle-icon ${isOpenDropdown ? "sidebar-toggle-icon-open" : ""}`}>
                    {isOpenDropdown ? "-" : "+"}
                  </span>
                </p>
              )}
            </div>
          </div>
          {isOpenDropdown && (
            <div className="ms-2">
              {item.subItems.map((subItem) => (
                <SidebarItem
                  key={subItem.id}
                  item={subItem}
                  isCollapsed={isCollapsed}
                  isSelected={location.pathname.includes(subItem.routePath)} // Mark only the active sub-item as selected
                  onSelect={onSelect}
                  isOpenDropdown={false} // Sub-items do not have nested sub-items, so keep dropdown closed
                  onToggleDropdown={() => {}} // Sub-items do not toggle
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Sidebar: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null); // Track the open dropdown

  useEffect(() => {
    const updateCollapseState = () => {
      const storedValue = localStorage.getItem("sidebarCollapsed");
      setIsCollapsed(storedValue === "true");
    };

    // Initial update
    updateCollapseState();

    // Set up event listener for storage changes
    window.addEventListener("storage", updateCollapseState);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener("storage", updateCollapseState);
    };
  }, []);

  const handleSidebarToggle = () => {
    const current = localStorage.getItem("sidebarCollapsed");
    const newValue = current === "true" ? "false" : "true";
    localStorage.setItem("sidebarCollapsed", newValue);
    setIsCollapsed(newValue === "true");
  };

  const handleItemClick = (id: string) => {
    setSelectedItemId(id); // Set the selected item
  };

  const handleToggleDropdown = (id: string) => {
    setOpenDropdownId((prevOpenId) => (prevOpenId === id ? null : id)); // Toggle dropdown
  };

  return (
    <nav className={`admin-sidebar sidebar-dynamic-height ${isCollapsed ? "collapsed" : ""}`}>
      <div className="">
        {!isCollapsed && (
          <img width={242} height={72} src="http://logos-download.com/wp-content/uploads/2016/07/DHL_logo.png" alt="logo" />
        )}
      </div>

      <div className="sidebar-links-container mt-4 px-2">
        {!isCollapsed && <p className="Navigation px-2 mb-1">Navigation</p>}
        {SidebarConst.map((item) => (
          <SidebarItem
            key={item.id}
            item={item}
            isCollapsed={isCollapsed}
            isSelected={selectedItemId === item.id.toString()}
            isOpenDropdown={openDropdownId === item.id.toString()} // Only one dropdown can be open at a time
            onSelect={handleItemClick}
            onToggleDropdown={handleToggleDropdown} // Toggle dropdown logic
          />
        ))}
      </div>
    </nav>
  );
};

export default Sidebar;


// export default Sidebar;
